import {
  getDebugDiagnosticsSnapshot,
  redactDebugValue,
  type DebugDiagnosticsSnapshot,
} from '@/lib/debugDiagnostics';
import type { Organization, OrganizationMember } from '@/types';
import type { User } from '@supabase/supabase-js';

interface DebugEnvironmentOptions {
  environment?: string;
  mode?: string;
  hostname?: string;
}

export interface DebugClipboardContext {
  user: User | null;
  organization: Organization | null;
  membership: OrganizationMember | null;
  organizations: Organization[];
  mspCustomerOrgs: Organization[];
  isMspOrg: boolean;
  isMspContext: boolean;
  mspHomeOrg: Organization | null;
  bootstrapSubscription: unknown;
  bootstrapSupplierLimit: unknown;
}

interface DebugClipboardPayloadOptions {
  context: DebugClipboardContext;
  location: Pick<Location, 'href' | 'origin' | 'hostname' | 'pathname' | 'search' | 'hash'>;
  document?: Pick<Document, 'title' | 'referrer' | 'visibilityState'>;
  navigator?: Pick<Navigator, 'userAgent' | 'language' | 'languages' | 'platform' | 'cookieEnabled'> & {
    connection?: unknown;
  };
  screen?: Pick<Screen, 'width' | 'height' | 'availWidth' | 'availHeight' | 'colorDepth' | 'pixelDepth'>;
  diagnostics?: DebugDiagnosticsSnapshot;
  now?: Date;
  environment?: string;
  mode?: string;
}

function getDebugBuildInfo() {
  return {
    version: typeof __APP_VERSION__ === 'undefined' ? 'unknown' : __APP_VERSION__,
    buildRef: typeof __APP_BUILD_REF__ === 'undefined' ? 'unknown' : __APP_BUILD_REF__,
    commitSha: typeof __APP_SHA__ === 'undefined' ? 'unknown' : __APP_SHA__,
    runId: typeof __APP_RUN_ID__ === 'undefined' ? 'unknown' : __APP_RUN_ID__,
    builtAtUtc: typeof __APP_BUILT_AT_UTC__ === 'undefined' ? 'unknown' : __APP_BUILT_AT_UTC__,
  };
}

export function resolveDebugEnvironmentName({
  environment = import.meta.env.VITE_SENTRY_ENVIRONMENT,
  mode = import.meta.env.MODE,
}: DebugEnvironmentOptions = {}): string {
  return environment || mode || 'unknown';
}

export function isNonProductionDebugEnvironment({
  environment,
  mode,
  hostname,
}: DebugEnvironmentOptions = {}): boolean {
  const resolvedEnvironment = resolveDebugEnvironmentName({ environment, mode }).toLowerCase();
  const resolvedHostname = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');

  if (resolvedEnvironment === 'production' || resolvedEnvironment === 'prod') {
    return false;
  }

  if (resolvedHostname === 'app.supply-guard.io') {
    return false;
  }

  return true;
}

function summarizeUser(user: User | null) {
  if (!user) {
    return null;
  }

  return {
    id: user.id,
    email: user.email ?? null,
    role: user.role ?? null,
    aud: user.aud ?? null,
    createdAt: user.created_at ?? null,
    lastSignInAt: user.last_sign_in_at ?? null,
    appMetadata: redactDebugValue(user.app_metadata ?? {}),
    userMetadata: redactDebugValue(user.user_metadata ?? {}),
  };
}

function summarizeOrganization(organization: Organization | null) {
  if (!organization) {
    return null;
  }

  return {
    id: organization.id,
    name: organization.name,
    slug: organization.slug ?? null,
    createdAt: organization.created_at ?? null,
    updatedAt: organization.updated_at ?? null,
    settings: redactDebugValue(organization.settings ?? {}),
  };
}

function summarizeOrganizationList(organizations: Organization[]) {
  return organizations.map((organization) => ({
    id: organization.id,
    name: organization.name,
    slug: organization.slug ?? null,
  }));
}

function summarizeMembership(membership: OrganizationMember | null) {
  if (!membership) {
    return null;
  }

  return {
    id: membership.id,
    organizationId: membership.organization_id,
    userId: membership.user_id,
    role: membership.role,
    createdAt: membership.created_at,
  };
}

function getStorageSnapshot(storage: Storage | undefined) {
  if (!storage) {
    return null;
  }

  const keys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key) {
      keys.push(key);
    }
  }

  return {
    keys: keys.sort(),
    selectedValues: Object.fromEntries(
      keys
        .filter((key) => ['currentOrgId', 'currentOrgUserId', 'isMspContext', 'mspHomeOrgId', 'currentViewMode'].includes(key))
        .map((key) => [key, redactDebugValue(storage.getItem(key), key)]),
    ),
  };
}

function getActiveElementSnapshot() {
  if (typeof document === 'undefined') {
    return null;
  }

  const element = document.activeElement;
  if (!element) {
    return null;
  }

  return {
    tagName: element.tagName,
    id: element.id || null,
    role: element.getAttribute('role'),
    ariaLabel: element.getAttribute('aria-label'),
    testId: element.getAttribute('data-testid'),
  };
}

function getElementText(element: Element | null) {
  const text = element?.textContent?.replace(/\s+/g, ' ').trim();
  return text || null;
}

function truncateUiText(value: string, maxLength = 2_000): string {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength)}…[truncated ${value.length - maxLength} chars]`;
}

const KNOWN_DIALOG_STATUS_SUFFIXES = ['Unclaimed', 'Claimed', 'Active', 'Inactive', 'Pending', 'Draft', 'Archived'] as const;

function splitTitleAndStatusBySuffix(rawTitle: string | null) {
  if (!rawTitle) {
    return { title: null, status: null };
  }

  for (const suffix of KNOWN_DIALOG_STATUS_SUFFIXES) {
    if (!rawTitle.endsWith(suffix)) {
      continue;
    }

    const title = rawTitle.slice(0, -suffix.length).trim();
    if (title.length > 0) {
      return { title, status: suffix };
    }
  }

  return { title: rawTitle, status: null };
}

function getElementBounds(element: Element) {
  if (!(element instanceof HTMLElement)) {
    return null;
  }

  const rect = element.getBoundingClientRect();
  return {
    x: Math.round(rect.x),
    y: Math.round(rect.y),
    width: Math.round(rect.width),
    height: Math.round(rect.height),
  };
}

function getDialogTitle(dialog: Element) {
  const labelledBy = dialog.getAttribute('aria-labelledby');
  if (labelledBy) {
    return getElementText(document.getElementById(labelledBy));
  }

  return getElementText(dialog.querySelector('[data-dialog-title], h1, h2, h3, [role="heading"]'));
}

function getDialogTitleElement(dialog: Element) {
  const labelledBy = dialog.getAttribute('aria-labelledby');
  if (labelledBy) {
    return document.getElementById(labelledBy);
  }

  return dialog.querySelector('[data-dialog-title], h1, h2, h3, [role="heading"]');
}

function getDialogTitleAndStatus(dialog: Element) {
  const titleElement = getDialogTitleElement(dialog);
  const rawTitle = getDialogTitle(dialog);

  if (!titleElement) {
    return splitTitleAndStatusBySuffix(rawTitle);
  }

  const childElementTexts = Array.from(titleElement.children)
    .map((child) => getElementText(child))
    .filter((value): value is string => Boolean(value));

  if (childElementTexts.length === 0) {
    return splitTitleAndStatusBySuffix(rawTitle);
  }

  const leadingTextNode = Array.from(titleElement.childNodes)
    .filter((node) => node.nodeType === Node.TEXT_NODE)
    .map((node) => node.textContent?.replace(/\s+/g, ' ').trim() ?? '')
    .find((value) => value.length > 0) ?? null;

  const status = childElementTexts.join(' ').trim() || null;
  if (leadingTextNode) {
    return {
      title: leadingTextNode,
      status,
    };
  }

  return splitTitleAndStatusBySuffix(rawTitle);
}

function getDialogDescription(dialog: Element) {
  const describedBy = dialog.getAttribute('aria-describedby');
  if (describedBy) {
    return getElementText(document.getElementById(describedBy));
  }

  return getElementText(dialog.querySelector('[data-dialog-description], [role="note"], p'));
}

function isElementVisible(element: Element) {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  const style = window.getComputedStyle(element);
  const rect = element.getBoundingClientRect();
  return style.display !== 'none'
    && style.visibility !== 'hidden'
    && style.opacity !== '0'
    && rect.width > 0
    && rect.height > 0;
}

function summarizeDialog(dialog: Element) {
  const titleSummary = getDialogTitleAndStatus(dialog);

  return {
    role: dialog.getAttribute('role'),
    ariaLabel: dialog.getAttribute('aria-label'),
    ariaModal: dialog.getAttribute('aria-modal'),
    title: titleSummary.title,
    status: titleSummary.status,
    description: getDialogDescription(dialog),
    testId: dialog.getAttribute('data-testid'),
    visible: isElementVisible(dialog),
    bounds: getElementBounds(dialog),
  };
}

function summarizeVisibleError(element: Element) {
  const text = getElementText(element);
  if (!text) {
    return null;
  }

  return {
    role: element.getAttribute('role'),
    ariaLabel: element.getAttribute('aria-label'),
    testId: element.getAttribute('data-testid'),
    text: truncateUiText(text),
    visible: isElementVisible(element),
    bounds: getElementBounds(element),
  };
}

function getVisibleErrorSnapshot() {
  if (typeof document === 'undefined') {
    return [];
  }

  const errorSelectors = [
    '[role="alert"]',
    '[aria-live="assertive"]',
    '[data-debug-capture="error"]',
    '[data-testid*="error" i]',
  ];

  const seen = new Set<Element>();
  return errorSelectors
    .flatMap((selector) => Array.from(document.querySelectorAll(selector)))
    .filter((element) => {
      if (seen.has(element) || !isElementVisible(element)) {
        return false;
      }
      seen.add(element);
      return true;
    })
    .slice(0, 10)
    .map(summarizeVisibleError)
    .filter((value): value is NonNullable<ReturnType<typeof summarizeVisibleError>> => Boolean(value));
}

function getOpenOverlaySnapshot() {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return {
      openDialogs: [],
      openMenus: [],
      openPopovers: [],
      visibleErrors: [],
      modalBackdropVisible: false,
    };
  }

  const openDialogs = Array.from(document.querySelectorAll('[role="dialog"], [role="alertdialog"]'))
    .filter(isElementVisible)
    .slice(0, 5)
    .map(summarizeDialog);
  const openMenus = Array.from(document.querySelectorAll('[role="menu"], [role="listbox"]'))
    .filter(isElementVisible)
    .slice(0, 5)
    .map((element) => ({
      role: element.getAttribute('role'),
      ariaLabel: element.getAttribute('aria-label'),
      testId: element.getAttribute('data-testid'),
      bounds: getElementBounds(element),
    }));
  const openPopovers = Array.from(document.querySelectorAll('[data-radix-popper-content-wrapper], [popover]:popover-open'))
    .filter(isElementVisible)
    .slice(0, 5)
    .map((element) => ({
      testId: element.getAttribute('data-testid'),
      bounds: getElementBounds(element),
    }));

  return {
    openDialogs,
    openMenus,
    openPopovers,
    visibleErrors: getVisibleErrorSnapshot(),
    modalBackdropVisible: Boolean(openDialogs.length),
  };
}

export function buildDebugClipboardPayload({
  context,
  location,
  document: documentRef = typeof document !== 'undefined' ? document : undefined,
  navigator: navigatorRef = typeof navigator !== 'undefined' ? navigator : undefined,
  screen: screenRef = typeof screen !== 'undefined' ? screen : undefined,
  diagnostics = getDebugDiagnosticsSnapshot(),
  now = new Date(),
  environment = import.meta.env.VITE_SENTRY_ENVIRONMENT,
  mode = import.meta.env.MODE,
}: DebugClipboardPayloadOptions) {
  const environmentName = resolveDebugEnvironmentName({ environment, mode });
  const buildInfo = getDebugBuildInfo();

  return {
    schemaVersion: 1,
    capturedAt: now.toISOString(),
    app: {
      name: 'supplyspark-ent',
      version: buildInfo.version,
      buildRef: buildInfo.buildRef,
      commitSha: buildInfo.commitSha,
      runId: buildInfo.runId,
      builtAtUtc: buildInfo.builtAtUtc,
    },
    environment: {
      name: environmentName,
      mode: mode || null,
      isProduction: !isNonProductionDebugEnvironment({
        environment: environmentName,
        mode,
        hostname: location.hostname,
      }),
      origin: location.origin,
      hostname: location.hostname,
    },
    screen: {
      url: location.href,
      path: `${location.pathname}${location.search}${location.hash}`,
      title: documentRef?.title ?? null,
      referrer: documentRef?.referrer ?? null,
      visibilityState: documentRef?.visibilityState ?? null,
      activeElement: getActiveElementSnapshot(),
      viewport: typeof window !== 'undefined'
        ? {
          width: window.innerWidth,
          height: window.innerHeight,
          devicePixelRatio: window.devicePixelRatio,
        }
        : null,
      display: screenRef
        ? {
          width: screenRef.width,
          height: screenRef.height,
          availWidth: screenRef.availWidth,
          availHeight: screenRef.availHeight,
          colorDepth: screenRef.colorDepth,
          pixelDepth: screenRef.pixelDepth,
        }
        : null,
    },
    ui: getOpenOverlaySnapshot(),
    user: summarizeUser(context.user),
    customer: summarizeOrganization(context.organization),
    membership: summarizeMembership(context.membership),
    msp: {
      isMspOrg: context.isMspOrg,
      isMspContext: context.isMspContext,
      homeOrg: summarizeOrganization(context.mspHomeOrg),
      customerOrgs: summarizeOrganizationList(context.mspCustomerOrgs),
      availableOrganizations: summarizeOrganizationList(context.organizations),
    },
    subscription: redactDebugValue(context.bootstrapSubscription),
    supplierLimit: redactDebugValue(context.bootstrapSupplierLimit),
    browser: {
      userAgent: navigatorRef?.userAgent ?? null,
      language: navigatorRef?.language ?? null,
      languages: navigatorRef?.languages ?? null,
      platform: navigatorRef?.platform ?? null,
      cookieEnabled: navigatorRef?.cookieEnabled ?? null,
      connection: redactDebugValue(navigatorRef?.connection ?? null),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    storage: {
      localStorage: typeof localStorage !== 'undefined' ? getStorageSnapshot(localStorage) : null,
      sessionStorage: typeof sessionStorage !== 'undefined' ? getStorageSnapshot(sessionStorage) : null,
    },
    diagnostics,
  };
}

export async function copyDebugClipboardPayload(context: DebugClipboardContext): Promise<string> {
  if (!navigator.clipboard?.writeText) {
    throw new Error('Clipboard API is unavailable in this browser context.');
  }

  const payload = buildDebugClipboardPayload({
    context,
    location: window.location,
  });
  const json = JSON.stringify(payload, null, 2);
  await navigator.clipboard.writeText(json);
  return json;
}

function buildDebugDownloadFilename(now: Date): string {
  const isoTimestamp = now.toISOString().replace(/[:.]/g, '-');
  return `debug-data-${isoTimestamp}.json`;
}

export function downloadDebugPayload(context: DebugClipboardContext): string {
  if (typeof document === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL || !URL.revokeObjectURL) {
    throw new Error('File download is unavailable in this browser context.');
  }

  const payload = buildDebugClipboardPayload({
    context,
    location: window.location,
  });
  const json = JSON.stringify(payload, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = buildDebugDownloadFilename(new Date());
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
  return json;
}
